import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useLiterals from 'utils/hooks/useLiterals';
import useConfiguration from 'hooks/useConfiguration';
import Layout from 'components/Layout';
import AppShell from 'components/Layout/AppShell';
import {
  getContracts, getSections,
} from 'modules/digitalization/actions';
import Box from 'components/UI/Box/Box';
import { formatDate } from 'utils/formatDate';
import { formatCurrency } from 'utils/formatCurrency';
import Button from 'components/UI/Button';
import Input from 'components/UI/Input';
import Select from 'components/UI/Select';
import MenuSwitch from 'components/UI/MenuSwitch';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import { ReactComponent as IconDownload } from 'assets/icons/download-white.svg';
import InlineError from 'components/UI/InlineError';
import { EFFECT_LIST_BOX_UP_IN_VIEW } from 'constants/effects';
import { motion } from 'framer-motion';
import DocumentViewer from './components/DocumentViewer';
import './styles.scss';

const SECTION_TABS = [
  { value: 'contratos', label: 'Contratos' },
  { value: 'apartados', label: 'Apartados' },
];

const CONTRACTS_FILTER = [
  { value: 'all', label: 'Todos' },
  { value: 'vigente', label: 'Vigente' },
  { value: 'pad', label: 'Periodo Adicional' },
  { value: 'autos', label: 'Autos' },
];

const ST_VIGENTE = 'vigente';
const ST_PAD = 'periodo adicional';
const ST_LIQUIDADO = 'liquidado';
const ST_CANCELADO = 'cancelado';
const ST_VENCIDO = 'vencido';

const PAGE_SIZE = 10;

const RAMO = {
  '01': 'Alhajas',
  '02': 'Relojes',
  '03': 'Varios',
  '04': 'Autos',
};
const ramoLabel = (r) => RAMO[r] || r;

const statusClass = (estatus) => {
  const s = estatus?.toLowerCase() || '';
  if (s === ST_VIGENTE) return 'status--vigente';
  if (s === ST_PAD) return 'status--pad';
  if (s === ST_LIQUIDADO) return 'status--liquidado';
  if (s === ST_CANCELADO) return 'status--cancelado';
  if (s === ST_VENCIDO) return 'status--vencido';
  return '';
};

const canDownload = (item) => item.contratoDigitalizado != null && item.isDigitalized === true;

const Digitalization = () => {
  const literals = useLiterals('digitalization');
  const contracts = useSelector((state) => state.digitalization.contracts);
  const sections = useSelector((state) => state.digitalization.sections);
  const error = useSelector((state) => state.digitalization.error);
  const dispatch = useDispatch();
  const { getConfigurationValue } = useConfiguration();

  const contractWarning = useMemo(() => {
    return getConfigurationValue('contractDigitalizationWarning') || '';
  }, [getConfigurationValue]);

  const [tab, setTab] = useState('contratos');
  const [search, setSearch] = useState({ temp: '', search: '' });
  const [viewerDoc, setViewerDoc] = useState(null);
  const [viewerDocType, setViewerDocType] = useState(null);
  const [viewerNoteId, setViewerNoteId] = useState(null);
  const [viewerPath, setViewerPath] = useState(null);
  const [viewerExternalOrder, setViewerExternalOrder] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [cf, setCf] = useState('all');
  const [loaded, setLoaded] = useState(false);
  const [page, setPage] = useState(1);

  // Vista de detalle: historial de pagos de un item
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    Promise.all([dispatch(getContracts()), dispatch(getSections())])
      .finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPage(1);
  }, [tab, cf, search.search]);

  // --- Contratos ---
  const fContracts = useMemo(() => {
    if (!cf) return [];
    let list = contracts;
    switch (cf) {
      case 'vigente': list = list.filter((c) => c.estatus?.toLowerCase() === ST_VIGENTE); break;
      case 'pad': list = list.filter((c) => c.estatus?.toLowerCase() === ST_PAD); break;
      case 'autos': list = list.filter((c) => c.contrato && Number(c.contrato[0]) === 8); break;
      case 'all':
      default: list = list.filter((c) => {
        const s = c.estatus?.toLowerCase();
        return s === ST_VIGENTE || s === ST_PAD || (c.contrato && Number(c.contrato[0]) === 8);
      }); break;
    }
    if (search.search) {
      const s = search.search.toLowerCase();
      list = list.filter((c) => (
        c.contrato?.toString().includes(s)
        || c.fechaContrato?.includes(s)
        || ramoLabel(c.ramo).toLowerCase().includes(s)
        || c.sucursal?.toLowerCase().includes(s)
      ));
    }
    return list;
  }, [contracts, cf, search.search]);

  // --- Apartados ---
  const fSections = useMemo(() => {
    let list = sections.filter((s) => s.desEstatus?.toLowerCase() === ST_VIGENTE);
    if (search.search) {
      const s = search.search.toLowerCase();
      list = list.filter((a) => a.contrato?.toString().includes(s) || a.nombreSucursal?.toLowerCase().includes(s));
    }
    return list;
  }, [sections, search.search]);

  const doSearch = () => setSearch({ ...search, search: search.temp });
  const onKey = (e) => { if (e.key === 'Enter') doSearch(); };

  const handleBack = () => {
    if (detail) { setDetail(null); }
  };

  // --- Descarga ---
  const paymentDownloadable = (item, payment, isContract) => {
    if (payment.cancelado) return false;
    if (payment.pagoDigitalizado != null) return true;
    if (!isContract) return true;
    if (payment.externalOrder) return true;
    return false;
  };

  const clearViewer = () => {
    setViewerOpen(false);
    setViewerDoc(null);
    setViewerDocType(null);
    setViewerNoteId(null);
    setViewerPath(null);
    setViewerExternalOrder(null);
  };

  const doDownload = (item, payment, isContract) => {
    clearViewer();
    if (isContract) {
      if (payment.pagoDigitalizado != null) {
        const ruta = payment.pagoDigitalizado.ruta_archivo;
        if (ruta && canDownload(item)) {
          const filePath = ruta.replace(/^.*\/Documentos\//, '');
          setViewerPath(filePath);
          setViewerOpen(true);
          return;
        }
        setViewerDoc(payment.documento);
        setViewerDocType(payment.tipoDocumento);
        setViewerOpen(true);
        return;
      }
      if (payment.externalOrder) {
        setViewerExternalOrder(payment.externalOrder);
        setViewerOpen(true);
        return;
      }
      setViewerDoc(payment.documento);
      setViewerDocType(payment.tipoDocumento);
      setViewerOpen(true);
    } else {
      setViewerNoteId(String(payment.idNotaVenta));
      setViewerOpen(true);
    }
  };

  // --- Vista de historial de pagos ---
  const renderPaymentHistory = () => {
    if (!detail) return null;

    const { item, payments, isContract } = detail;
    const sorted = [...(payments || [])].sort((a, b) => {
      const da = isContract ? new Date(a.fechaOperacion) : new Date(a.fechaPago);
      const db = isContract ? new Date(b.fechaOperacion) : new Date(b.fechaPago);
      return db - da;
    });

    const label = isContract ? item.contrato : item.contrato;

    return (
      <div className='payment-history'>
        <h3 className='payment-history__title'>
          {`${label || ''} — ${literals.paymentsHistory}`}
          {isContract && (
            <span className='payment-history__chip'>
              {canDownload(item) ? literals.digitalized : literals.notDigitalized}
            </span>
          )}
        </h3>

        {canDownload(item) && (
          <div className='mb-4'>
            <Button
              size='sm'
              type='primary'
              onClick={() => {
                const ruta = item.contratoDigitalizado.ruta_archivo;
                if (!ruta) return;
                const path = ruta.replace(/^.*\/Documentos\//, '');
                setViewerDoc(null);
                setViewerDocType(null);
                setViewerNoteId(null);
                setViewerPath(path);
                setViewerOpen(true);
              }}
            >
              <IconDownload width={14} height={14} color='white' fill='white' />
              <span className='ms-1'>{literals.downloadDigitalizedDocument}</span>
            </Button>
          </div>
        )}

        {sorted.length === 0 ? (
          <Box padding='md' className='text-center'>
            <p className='text-neutral-500'>{literals.noPaymentsHistory}</p>
          </Box>
        ) : (
          sorted.map((p, idx) => {
            const dl = paymentDownloadable(item, p, isContract);
            const dateField = isContract ? 'fechaOperacion' : 'fechaPago';
            const amount = isContract ? p.montoTotal : p.monto;
            const tc = isContract ? p.tipoDocumento : p.idTipoPago;
            const pt = p.tipoPago;
            const cajaLabel = (() => {
              if (!p.caja) return '';
              if (p.caja === 'Pago por portal web o App MLS') return 'pago en linea';
              if (p.caja === 'Comercial') return 'Pago en sucursal';
              return p.caja;
            })();

            return (
              <motion.div key={idx} {...EFFECT_LIST_BOX_UP_IN_VIEW} custom={idx}>
                <Box className='payment-card mb-2' padding='sm'>
                  <div className='payment-card__row'>
                    <div className='payment-card__info'>
                      <span className='payment-card__date'>
                        <span className='payment-card__label'>
                          {literals.paymentDateLabel}
                          :
                        </span>
                        {' '}
                        {formatDate(p[dateField], 'YYYY-MM-DD')}
                      </span>
                      <span className='payment-card__amount'>
                        <span className='payment-card__label'>
                          {literals.amountLabel}
                          :
                        </span>
                        {' '}
                        {formatCurrency(amount)}
                      </span>
                    </div>
                    <div className='payment-card__detail'>
                      <span className='payment-card__type'>
                        <span className='payment-card__label'>
                          {literals.typeLabel}
                          :
                        </span>
                        {' '}
                        {pt || ''}
                        {tc != null ? ` (${String(tc).padStart(2, '0')})` : ''}
                        {p.idOperacion ? ` · ${p.idOperacion}` : ''}
                      </span>
                      {cajaLabel && (
                        <span className='payment-card__caja'>
                          <span className='payment-card__label'>
                            {literals.cajaLiteralsLabel}
                            :
                          </span>
                          {' '}
                          {cajaLabel}
                        </span>
                      )}
                      {isContract && p.tipoMovimiento && (
                        <span className='payment-card__detail-item'>
                          <span className='payment-card__label'>
                            {literals.movementLabel}
                            :
                          </span>
                          {' '}
                          {p.tipoMovimiento}
                        </span>
                      )}
                      {p.cancelado && (
                        <span className='payment-card__detail-item status--cancelado'>{literals.cancelled}</span>
                      )}
                    </div>
                    {dl && (
                      <div className='payment-card__download'>
                        <Button
                          size='sm'
                          type='primary'
                          onClick={() => doDownload(item, p, isContract)}
                        >
                          <IconDownload width={14} height={14} color='white' fill='white' />
                          <span className='ms-1'>{literals.reprintShort}</span>
                        </Button>
                      </div>
                    )}
                  </div>
                </Box>
              </motion.div>
            );
          })
        )}
      </div>
    );
  };

  // --- Cards de la lista principal ---
  const renderContractCard = (c, idx) => (
    <motion.div key={c.contrato || idx} {...EFFECT_LIST_BOX_UP_IN_VIEW} custom={idx}>
      <Box className='digital-card mb-2' padding='sm' onClick={() => setDetail({ item: c, payments: c.pagos, isContract: true })}>
        <div className='digital-card__row'>
          <div className='digital-card__info'>
            <div className='digital-card__header'>
              <span className='digital-card__label'>
                {literals.contractLabel}
                :
              </span>
              <span className='digital-card__number'>{c.contrato || '—'}</span>
              <span className={`digital-card__status ${statusClass(c.estatus)}`}>{c.estatus || ''}</span>
            </div>
            <div className='digital-card__meta'>
              <span className='digital-card__label'>
                {literals.ramoLiteralsLabel}
                :
              </span>
              <span>{ramoLabel(c.ramo)}</span>
              <span className='digital-card__dot'>·</span>
              <span className='digital-card__label'>
                {literals.branchLabel}
                :
              </span>
              <span>{c.sucursal || ''}</span>
              <span className='digital-card__dot'>·</span>
              <span className='digital-card__label'>
                {literals.dateLabel}
                :
              </span>
              <span>{formatDate(c.fechaContrato, 'YYYY-MM-DD')}</span>
            </div>
            <div className='digital-card__amount'>
              <span className='digital-card__label'>
                {literals.loanLabel}
                :
              </span>
              {' '}
              {c.prestamo ? formatCurrency(c.prestamo) : ''}
            </div>
          </div>
          <span className='digital-card__arrow'>›</span>
        </div>
      </Box>
    </motion.div>
  );

  const renderSectionCard = (s, idx) => (
    <motion.div key={s.contrato || idx} {...EFFECT_LIST_BOX_UP_IN_VIEW} custom={idx}>
      <Box className='digital-card mb-2' padding='sm' onClick={() => setDetail({ item: s, payments: s.pagos, isContract: false })}>
        <div className='digital-card__row'>
          <div className='digital-card__info'>
            <div className='digital-card__header'>
              <span className='digital-card__label'>
                {literals.sectionLabel}
                :
              </span>
              <span className='digital-card__number'>{s.contrato || '—'}</span>
              <span className={`digital-card__status ${statusClass(s.desEstatus)}`}>{s.desEstatus || ''}</span>
            </div>
            <div className='digital-card__meta'>
              <span className='digital-card__label'>
                {literals.branchLabel}
                :
              </span>
              <span>{s.nombreSucursal || ''}</span>
              <span className='digital-card__dot'>·</span>
              <span className='digital-card__label'>
                {literals.dateLabel}
                :
              </span>
              <span>{formatDate(s.fechaInicio, 'YYYY-MM-DD')}</span>
            </div>
            <div className='digital-card__amount'>
              <span className='digital-card__label'>
                {literals.amountLabel}
                :
              </span>
              {' '}
              {formatCurrency(s.montoVenta)}
            </div>
          </div>
          <span className='digital-card__arrow'>›</span>
        </div>
      </Box>
    </motion.div>
  );

  const renderEmpty = (noRes, noData) => {
    if (error) return <Box padding='md' className='text-center'><InlineError error={literals.errorLoadData} /></Box>;
    if (!loaded) return <Box padding='md' className='text-center'><p className='text-neutral-500'>{literals.loading}</p></Box>;
    return <Box padding='md' className='text-center empty-state'><p className='empty-state__text'>{search.search ? literals[noRes] : literals[noData]}</p></Box>;
  };

  // --- Paginación ---
  const getCurrentList = () => {
    switch (tab) {
      case 'contratos': return fContracts;
      case 'apartados': return fSections;
      default: return [];
    }
  };

  const currentList = getCurrentList();
  const totalPages = Math.ceil(currentList.length / PAGE_SIZE);
  const paginatedList = currentList.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const paginatedContracts = tab === 'contratos' ? paginatedList : [];
  const paginatedSections = tab === 'apartados' ? paginatedList : [];

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
      const result = [];
      const maxVisible = 5;

      if (totalPages <= maxVisible) {
        for (let i = 1; i <= totalPages; i += 1) {
          result.push(i);
        }
      } else {
        result.push(1);
        let start = Math.max(2, page - 1);
        let end = Math.min(totalPages - 1, page + 1);

        if (page <= 3) {
          end = Math.min(maxVisible - 1, totalPages - 1);
        }
        if (page >= totalPages - 2) {
          start = Math.max(2, totalPages - (maxVisible - 2));
        }

        if (start > 2) result.push('...');
        for (let i = start; i <= end; i += 1) {
          result.push(i);
        }
        if (end < totalPages - 1) result.push('...');
        result.push(totalPages);
      }
      return result;
    };

    const pageNumbers = getPageNumbers();

    return (
      <div className='pagination'>
        <button
          type='button'
          className='pagination__btn'
          disabled={page <= 1}
          onClick={() => setPage(page - 1)}
        >
          ‹
        </button>
        {pageNumbers.map((p, idx) => (
          p === '...' ? (
            <span key={`dots-${idx}`} className='pagination__dots'>...</span>
          ) : (
            <button
              type='button'
              key={p}
              className={`pagination__btn ${p === page ? 'pagination__btn--active' : ''}`}
              onClick={() => setPage(p)}
            >
              {p}
            </button>
          )
        ))}
        <button
          type='button'
          className='pagination__btn'
          disabled={page >= totalPages}
          onClick={() => setPage(page + 1)}
        >
          ›
        </button>
      </div>
    );
  };

  // --- Si hay detalle abierto mostramos solo eso ---
  if (detail) {
    return (
      <Layout title={literals.title} bottomMenu onBack={handleBack} className='digitalization-page'>
        {renderPaymentHistory()}
        {viewerOpen && (viewerDoc || viewerNoteId || viewerPath || viewerExternalOrder) && <DocumentViewer documentId={viewerDoc} documentType={viewerDocType} noteId={viewerNoteId} path={viewerPath} externalOrder={viewerExternalOrder} onClose={clearViewer} />}
      </Layout>
    );
  }

  return (
    <Layout title={literals.title} bottomMenu onBack className='digitalization-page'>
      <AppShell>
        <div className='digitalization'>
          <MenuSwitch options={SECTION_TABS} selected={tab} onChange={(v) => { setTab(v); setSearch({ temp: '', search: '' }); }} />

          {tab === 'contratos' && contractWarning && (
            <div className='digitalization-warning'>
              <p>{contractWarning}</p>
            </div>
          )}

          <div className='searcher'>
            <Input placeholder={literals.searchPlaceholder} value={search.temp} onChange={(v) => setSearch({ ...search, temp: v })} onKeyDown={onKey} />
            <Button className='searcher__btn no-hover-btn' isIconButton onClick={doSearch}><SearchIcon /></Button>
          </div>

          <div className='filter-section mb-4'>
            {tab === 'contratos' && <Select value={cf} options={CONTRACTS_FILTER} onChange={setCf} placeholder={literals.filterPlaceholder} />}
          </div>

          <div className='digital-list'>
            {tab === 'contratos' && (paginatedContracts.length > 0 ? paginatedContracts.map(renderContractCard) : renderEmpty('noContractsFound', 'noContracts'))}
            {tab === 'apartados' && (paginatedSections.length > 0 ? paginatedSections.map(renderSectionCard) : renderEmpty('noSectionsFound', 'noSections'))}
          </div>
          {renderPagination()}
        </div>
        {viewerOpen && (viewerDoc || viewerNoteId || viewerExternalOrder) && <DocumentViewer documentId={viewerDoc} documentType={viewerDocType} noteId={viewerNoteId} externalOrder={viewerExternalOrder} onClose={clearViewer} />}
      </AppShell>
    </Layout>
  );
};

export default Digitalization;
